import React from 'react';
import { Trophy, Clock } from 'lucide-react';
import { calcProductionPace } from '../kpiUtils';

const getProgressColor = (pct) => {
  if (pct >= 90) return { bar: 'bg-emerald-500', text: 'text-emerald-600' };
  if (pct >= 60) return { bar: 'bg-amber-500', text: 'text-amber-600' };
  return { bar: 'bg-red-500', text: 'text-red-600' };
};

const MiniBar = ({ pct, colorClass }) => (
  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
    <div className={`h-full rounded-full transition-all duration-500 ${colorClass}`} style={{ width: `${Math.min(100, pct)}%` }} />
  </div>
);

const WorkerRow = ({ perf, rank }) => {
  const pct = Number(perf.percentage || 0);
  const achieved = Number(perf.total_achieved || 0);
  const target = Number(perf.total_target || 0);
  const colors = getProgressColor(pct);
  const initials = (perf.worker_name || '?').slice(0, 2).toUpperCase();
  const avatarBg = pct >= 100
    ? 'bg-emerald-100 text-emerald-700'
    : pct > 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500';

  return (
    <div className="flex items-center gap-3 py-2 border-b border-slate-100 last:border-0">
      <span className="text-[10px] text-slate-400 w-4 text-right shrink-0">{rank}</span>
      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold shrink-0 ${avatarBg}`}>
        {initials}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-sm font-medium text-slate-800 truncate">{perf.worker_name}</span>
          {pct >= 100 && (
            <span className="flex items-center gap-0.5 text-[9px] bg-amber-100 text-amber-700 px-1 py-0.5 rounded font-bold uppercase whitespace-nowrap">
              <Trophy className="w-2.5 h-2.5" /> Overachiever
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <MiniBar pct={pct} colorClass={colors.bar} />
          <span className={`text-xs font-semibold tabular-nums w-10 text-right ${colors.text}`}>{pct}%</span>
        </div>
      </div>
      <div className="text-right shrink-0">
        <p className="text-sm font-bold text-slate-800 tabular-nums">{achieved.toLocaleString()}</p>
        <p className="text-[10px] text-slate-400">/ {target.toLocaleString()}</p>
      </div>
    </div>
  );
};

const WorkLogEntry = ({ log }) => {
  const qty = Number(log.quantity_completed || log.quantity_produced || 0);
  const item = log.item_name || log.reagent_name || log.hardware_name || `Item #${log.reagent_id || log.hardware_id || '?'}`;
  return (
    <div className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0 text-xs">
      <div className="flex items-center gap-2 min-w-0">
        <Clock className="w-3 h-3 text-slate-400 shrink-0" />
        <span className="text-slate-500 tabular-nums shrink-0">{log.start_time || '—'}–{log.end_time || 'now'}</span>
        <span className="font-medium text-slate-700 truncate">{log.worker_name}</span>
        <span className="text-slate-400 truncate hidden sm:block">{item}</span>
      </div>
      <span className="font-bold text-emerald-600 tabular-nums shrink-0">+{qty}</span>
    </div>
  );
};

export default function WorkforceProduction({ data }) {
  if (!data) return null;
  const { teamProgress, crewPerformance, workLogs } = data;
  const pct = Math.max(0, Number(teamProgress.percentage || 0));
  const colors = getProgressColor(pct);
  const pace = calcProductionPace(teamProgress.totalAchieved, teamProgress.totalTarget);

  const sortedCrew = [...crewPerformance].sort((a, b) => Number(b.percentage || 0) - Number(a.percentage || 0));
  const recentLogs = [...(workLogs || [])].sort((a, b) => (b.start_time || '').localeCompare(a.start_time || '')).slice(0, 8);

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm h-full">
      <div className="px-5 py-4 border-b border-slate-200">
        <h3 className="text-base font-semibold text-slate-800">Workforce & Production</h3>
      </div>
      <div className="p-5 space-y-5">
        {/* Team progress */}
        <div>
          <div className="flex justify-between items-baseline mb-1">
            <p className="text-sm font-medium text-slate-700">Today's Target</p>
            <p className="text-sm text-slate-500 tabular-nums">
              {teamProgress.totalAchieved.toLocaleString()} / {teamProgress.totalTarget.toLocaleString()} units
            </p>
          </div>
          <div className="h-3 rounded-full bg-slate-100 overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-700 ${colors.bar}`} style={{ width: `${Math.min(100, pct)}%` }} />
          </div>
          <div className="flex justify-between mt-1 text-xs text-slate-500">
            <span className={`font-semibold ${colors.text}`}>{pct}% complete</span>
            {pace.workdayProgress > 0 && (
              <span>Projected EOD: <strong className="text-slate-700">{pace.projectedEOD.toLocaleString()}</strong></span>
            )}
          </div>
        </div>

        {/* Crew leaderboard */}
        {sortedCrew.length > 0 ? (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2">Crew Leaderboard</p>
            <div className="max-h-[220px] overflow-y-auto">
              {sortedCrew.map((perf, i) => (
                <WorkerRow key={perf.worker_id || perf.worker_name} perf={perf} rank={i + 1} />
              ))}
            </div>
          </div>
        ) : (
          <p className="text-sm text-slate-400 italic">No crew performance data.</p>
        )}

        {/* Recent work logs */}
        {recentLogs.length > 0 && (
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-2">
              Recent Activity ({recentLogs.length})
            </p>
            <div className="max-h-[160px] overflow-y-auto">
              {recentLogs.map((log) => <WorkLogEntry key={log.id} log={log} />)}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
